export interface TaskReminderSettings {
  enabled: boolean
  emailEnabled: boolean
  inAppEnabled: boolean
  firstLoginEnabled: boolean
  afternoonEnabled: boolean
  closingEnabled: boolean
  timeZone: string
  workdays: number[]
  afternoonTime: string | null
  closingTime: string | null
  approachingHours: number
}

export interface TaskReminderSettingsResponse {
  settings: TaskReminderSettings
  delivery: { emailConfigured: boolean; schedulerConfigured: boolean }
  recentRuns?: RecentReminderRun[]
}

export interface RecentReminderRun {
  localDate: string
  slot: 'FIRST_LOGIN' | 'AFTERNOON' | 'CLOSING'
  emailStatus: 'PENDING' | 'PROCESSING' | 'RETRY' | 'ACCEPTED' | 'FAILED' | 'UNKNOWN' | 'SKIPPED'
  count: number
}

export interface TaskReminderForm extends Omit<TaskReminderSettings, 'afternoonTime' | 'closingTime' | 'approachingHours'> {
  afternoonTime: string
  closingTime: string
  approachingHours: string
}

export interface FirstLoginReminderResponse {
  created: boolean
  notification?: { id: string; title: string; body: string; link: string | null }
  reason?: string
}

export function taskReminderForm(settings: TaskReminderSettings): TaskReminderForm {
  return {
    ...settings,
    workdays: [...settings.workdays],
    afternoonTime: settings.afternoonTime ?? '',
    closingTime: settings.closingTime ?? '',
    approachingHours: String(settings.approachingHours),
  }
}

export function buildTaskReminderSettings(form: TaskReminderForm): TaskReminderSettings {
  const timeZone = form.timeZone.trim()
  try {
    if (timeZone !== 'UTC' && !/^[A-Za-z_]+(?:\/[A-Za-z0-9_+\-]+)+$/.test(timeZone)) throw new Error('Invalid IANA time zone')
    new Intl.DateTimeFormat('en', { timeZone }).format()
  } catch {
    throw new Error('Enter a valid organisation time zone, such as Africa/Lagos or Etc/UTC.')
  }

  if (!form.workdays.length || form.workdays.some((day) => !Number.isInteger(day) || day < 1 || day > 7) ||
    new Set(form.workdays).size !== form.workdays.length) {
    throw new Error('Select at least one workday, with each day selected only once.')
  }

  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/
  for (const [label, value] of [['Mid-afternoon', form.afternoonTime], ['Closing', form.closingTime]]) {
    if (value && !validTime.test(value)) throw new Error(`${label} time must use a valid 24-hour HH:mm time.`)
  }
  if (form.afternoonEnabled && form.closingEnabled && form.afternoonTime && form.closingTime &&
    form.afternoonTime >= form.closingTime) {
    throw new Error('Mid-afternoon time must be earlier than closing time.')
  }

  if (!/^\d+$/.test(form.approachingHours) || !Number.isInteger(Number(form.approachingHours)) ||
    Number(form.approachingHours) < 1 || Number(form.approachingHours) > 168) {
    throw new Error('Approaching deadline window must be a whole number from 1 to 168 hours.')
  }

  if (form.enabled) {
    if (!form.emailEnabled && !form.inAppEnabled) throw new Error('Select email, in-app notifications, or both before enabling reminders.')
    if (!form.firstLoginEnabled && !form.afternoonEnabled && !form.closingEnabled) {
      throw new Error('Select at least one reminder moment before enabling reminders.')
    }
    if (form.afternoonEnabled && !form.afternoonTime) throw new Error('Choose a mid-afternoon time before enabling reminders.')
    if (form.closingEnabled && !form.closingTime) throw new Error('Choose a closing time before enabling reminders.')
  }

  return {
    ...form,
    timeZone,
    workdays: [...form.workdays].sort((a, b) => a - b),
    afternoonTime: form.afternoonTime || null,
    closingTime: form.closingTime || null,
    approachingHours: Number(form.approachingHours),
  }
}

export function createFirstLoginReminderCoordinator(request: () => Promise<FirstLoginReminderResponse>) {
  const inFlight = new Map<string, Promise<FirstLoginReminderResponse>>()
  const shownNotifications = new Set<string>()

  return {
    check(userId: string): Promise<FirstLoginReminderResponse> {
      const existing = inFlight.get(userId)
      if (existing) return existing

      // Share overlapping focus/visibility/profile checks. Never cache a date or
      // successful check: only the server knows the organisation's current day.
      const pending = Promise.resolve().then(request).finally(() => {
        if (inFlight.get(userId) === pending) inFlight.delete(userId)
      })
      inFlight.set(userId, pending)
      return pending
    },
    takeNotification(userId: string, result: FirstLoginReminderResponse) {
      if (!result.created || !result.notification) return undefined
      const key = `${userId}:${result.notification.id}`
      if (shownNotifications.has(key)) return undefined
      shownNotifications.add(key)
      return result.notification
    },
  }
}
